import React, { useEffect, useRef, useState } from "react"
import { UseFormRegister, UseFormSetValue } from "react-hook-form"
import ContentLoader from "react-content-loader"
import { AnimatePresence } from "framer-motion"
import { styled } from "@mui/material/styles"
import { CLOSE_FULL_SCREEN_IMAGE_MODAL, DISCARD_IMAGE_CROP, SAVE_CROPPED_IMAGE } from "constants/events"
import { uploadImage } from "utils/componentsUtils"
import useEventListener from "hooks/useEventListener"
import UserIcon from "components/atoms/_icons/UserIcon"
import EditIcon from "components/atoms/_icons/EditIcon"
import AddImageIcon from "components/atoms/_icons/AddImageIcon"
import FullScreenSelectedImageModal from "components/molecules/FullScreenSelectedImageModal"
import ImageCropperModal from "components/atoms/ImageCropperModal"

type ProfileImageSelectionProps = {
  id: string;
  defaultValue: File | string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  onChange: (value: File | null) => void;
}

export default function ProfileImageSelection(props: ProfileImageSelectionProps) {
  const { id, defaultValue, register, setValue, onChange } = props

  const [userAvatar, setUserAvatar] = useState<string | null>(null)
  const [chosenAvatar, setChosenAvatar] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [showEnlargedAvatarModal, setShowEnlargedAvatarModal] = useState<boolean>(false)
  const [showCropModal, setShowCropModal] = useState<boolean>(false)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  const containerRef = useRef<HTMLLabelElement | null>(null)

  useEventListener(document, CLOSE_FULL_SCREEN_IMAGE_MODAL, handleCloseFullScreenAvatar)

  useEventListener(document, DISCARD_IMAGE_CROP, handleDiscardImageCrop)

  useEventListener(document, SAVE_CROPPED_IMAGE, handleSaveCroppedImage)

  useEffect(() => {
    setAvatarBasedOnDefaultValue()
  }, [defaultValue])

  function setAvatarBasedOnDefaultValue() {
    if (defaultValue && typeof defaultValue === "string" && defaultValue.length) {
      setSavedAvatarAsDefault()
    } else if ((defaultValue instanceof File && defaultValue.size === 0) || (typeof defaultValue === "string" && defaultValue.length === 0)) {
      resetAvatar()
    } else if (defaultValue instanceof File && defaultValue.size > 0) {
      setAvatarFileAsContainerBackground()
    }
  }

  function setSavedAvatarAsDefault() {
    if (!containerRef.current || typeof defaultValue !== "string") return
    const imageSrc = `${process.env.REACT_APP_API_URL}/${defaultValue.replace(/ /g, "%20")}`
    setUserAvatar(imageSrc)
  }

  function resetAvatar() {
    if (!containerRef.current) return
    containerRef.current.style.backgroundImage = "none"
    setUserAvatar(null)
  }

  function setAvatarFileAsContainerBackground() {
    if (!containerRef.current) return
    if (defaultValue && typeof defaultValue !== "string") {
      const fileReader = new FileReader()

      fileReader.onload = () => {
        if (!containerRef.current) return
        containerRef.current.style.backgroundImage = `url(${fileReader.result as string})`
        containerRef.current.style.backgroundSize = "cover"
        setUserAvatar(fileReader.result as string)
      }

      fileReader.readAsDataURL(defaultValue)
    }
  }


  function handleSelectAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    if (!containerRef.current) return
    const file = event.target.files?.[0]
    if (file) {
      setFileName(file.name)
      const reader = new FileReader()
      reader.onload = () => {
        setChosenAvatar(reader.result as string)
        setShowCropModal(true)
        setShowEnlargedAvatarModal(false)
      }
      reader.readAsDataURL(file)
    }
  }

  function handleDeleteAvatar() {
    setUserAvatar(null)
    setChosenAvatar(null)
    onChange(null)
    setValue(id, null, { shouldDirty: true })
    if (containerRef.current) {
      containerRef.current.style.backgroundImage = ""
    }
    document.dispatchEvent(new CustomEvent(CLOSE_FULL_SCREEN_IMAGE_MODAL))
  }

  function handleContainerClicked() {
    if (!userAvatar) return
    setShowEnlargedAvatarModal(true)
  }

  function handleCloseFullScreenAvatar() {
    if (showEnlargedAvatarModal) {
      setShowEnlargedAvatarModal(false)
    }
  }

  function handleDiscardImageCrop() {
    if (showCropModal) {
      setShowCropModal(false)
      setChosenAvatar(null)
    }
  }

  function handleSaveCroppedImage(event: CustomEvent) {
    if (!fileName || !containerRef.current) return
    const { blob } = event.detail
    const file = new File([blob], fileName, { type: "image/jpeg" })
    const customEventObj = { target: { files: [file] } }
    uploadImage(customEventObj, containerRef.current, setUserAvatar, onChange)
    setShowCropModal(false)
    setChosenAvatar(null)
    setShowEnlargedAvatarModal(false)
  }

  function handleImageLoad() {
    if (!containerRef.current) return
    setIsLoaded(true)
    containerRef.current.style.backgroundImage = `url("${userAvatar}")`
  }

  return (
    <>
      <Container
        ref={containerRef}
        onClick={handleContainerClicked}
      >
        {!userAvatar && <>
            <input
                id="image"
                type="file"
                accept="image/*"
                {...register(id)}
                onChange={handleSelectAvatar}
            />
            <UserIcon/>
        </>}
        {userAvatar && <>
          {!isLoaded && <LoaderWrapper>
              <ContentLoader>
                  <rect x="0" y="0" width="100%" height="100%"/>
              </ContentLoader>
          </LoaderWrapper>}
            <img
                src={userAvatar || ""}
                alt={"avatar"}
                onLoad={handleImageLoad}
                style={{
                  display: "none"
                }}
            />
        </>}
        <EditButtonContainer>
          {userAvatar ? <EditIcon/> : <AddImageIcon/>}
        </EditButtonContainer>
      </Container>
      <AnimatePresence>
        {chosenAvatar && showCropModal && <ImageCropperModal
            src={chosenAvatar}
        />}
      </AnimatePresence>
      <FullScreenSelectedImageModal
        show={!!userAvatar && showEnlargedAvatarModal}
        src={userAvatar}
        onImageChange={handleSelectAvatar}
        onImageDelete={handleDeleteAvatar}
      />
    </>
  )
}

const Container = styled("label")(({ theme }) => ({
  width: "144px",
  height: "144px",
  borderRadius: "50%",
  backgroundColor: theme.palette.gray["4"],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  marginBottom: "50px",
  backgroundSize: "cover",
  backgroundPosition: "center",

  "& input": {
    display: "none"
  },

  "& svg": {
    stroke: theme.palette.gray["9"]
  }
}))

const EditButtonContainer = styled("div")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  border: "2px solid #FFFFFF",
  backgroundColor: theme.palette.gray["6"],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "70%",
  right: "65%",

  "& svg": {
    stroke: theme.palette.primary["6"]
  }
}))

const LoaderWrapper = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  overflow: "hidden",

  "& svg": {
    width: "100% !important",
    height: "100% !important",
    stroke: "transparent !important"
  }
}))
